import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../context/ProductsContext';
import Footer from '../components/Footer';

const MedicalAlertsPage: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const { products: apiProducts } = useProducts();
  const [includedImage, setIncludedImage] = useState<string>('');
  const [productImages, setProductImages] = useState<{[key: string]: string}>({});

  useEffect(() => {
    // Fetch images and map product images
    fetch(`${API_URL}/api/images`)
      .then(res => res.json())
      .then((data: any) => {
        // Create a lookup map for product images
        const imageMap: {[key: string]: string} = {};
        
        apiProducts.forEach(product => {
          if (product.imageFilename) {
            // Try exact match first
            let img = data.images.find((img: any) => 
              img.filename === product.imageFilename ||
              img.filename?.toLowerCase() === product.imageFilename?.toLowerCase()
            );
            
            // If no exact match, try fuzzy matching by product shortName
            if (!img && product.shortName) {
              const searchTerms = [
                product.shortName.replace(/-/g, ''),
                product.shortName.replace(/-/g, ' '),
                product.shortName,
                product.name.toLowerCase().replace(/[^a-z0-9]/g, ''),
                product.name.toLowerCase().replace(/[^a-z0-9]/g, ' '),
              ];
              
              img = data.images.find((img: any) => {
                const lowerFilename = img.filename?.toLowerCase() || '';
                return searchTerms.some(term => lowerFilename.includes(term));
              });
            }
            
            if (img) {
              imageMap[product.shortName] = `${API_URL}/api/images/${img.id}`;
              console.log(`[MedicalAlerts] Found image for ${product.shortName}: ${img.filename}`);
            } else {
              console.warn(`[MedicalAlerts] No image found for ${product.shortName} (looking for: ${product.imageFilename})`);
            }
          } else {
            console.warn(`[MedicalAlerts] Product ${product.shortName} has no imageFilename set`);
          }
        });
        
        console.log('[MedicalAlerts] Product images mapped:', Object.keys(imageMap));
        setProductImages(imageMap);

        // Look for included section image - woman with wrist alert
        const included = data.images.find((img: any) => 
          img.filename.toLowerCase().replace(/[^a-z0-9]/g, '').includes('596inhomewomanwrist') ||
          img.filename.toLowerCase().includes('womanwrist') ||
          img.filename.toLowerCase().includes('inhomewomanwrist')
        );
        if (included) setIncludedImage(`${API_URL}/api/images/${included.id}`);
      })
      .catch(err => console.error('Error fetching images:', err));
  }, [API_URL, apiProducts]);

  return (
    <div className="medical-alerts-page">
      {/* Hero Section */}
      <section className="medical-alerts-hero-section">
        <div className="container">
          <h1 className="medical-alerts-hero-title">Medical Alert Systems- Call us for our Lowest Price Guarantee</h1>
        </div>
      </section>

      {/* Products Section */}
      <section className="medical-alerts-products-section">
        <div className="medical-alerts-products-container">
          <div className="pricing-device-grid">
            {apiProducts
              .filter(p => p.isActive && !p.shortName.includes('-fall'))
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map(product => {
                const productPath = `/products/${product.shortName}`;
                // Get annual plan price
                const annualPlan = product.pricingPlans?.find((p: any) => p.billingCycle === 'annual');
                const price = annualPlan?.monthlyPrice || product.basePrice || 0;
                
                // Get product-specific features
                const getFeatures = () => {
                  if (product.shortName === 'ez-care') return ['Landline', 'Fall Detection Available'];
                  if (product.shortName === 'ez-care-plus') return ['Wireless', 'Fall Detection Available'];
                  if (product.shortName === 'lifebeacon') return ['Phone Not Required', 'Fall Detection Available', 'Built-In Speaker'];
                  return [];
                };
                
                return (
                  <div key={product.id} className="pricing-device-card">
                    <Link
                      to={productPath}
                      className="pricing-device-image-link"
                      aria-label={`View ${product.name} details`}
                    >
                      <div className="pricing-device-image-wrapper">
                        {productImages[product.shortName] ? (
                          <img src={productImages[product.shortName]} alt={product.name} className="pricing-device-image" />
                        ) : (
                          <div className="pricing-device-placeholder">
                            <svg width="200" height="150" viewBox="0 0 200 150" fill="none">
                              <rect width="200" height="150" fill="#f0f0f0"/>
                            </svg>
                          </div>
                        )}
                      </div>
                    </Link>
                    <h3 className="pricing-device-name">
                      <Link to={productPath} className="pricing-device-name-link">
                        {product.name}
                      </Link>
                    </h3>
                    <p className="pricing-device-price">FROM ${price.toFixed(2)}</p>
                    <p className="pricing-device-description">
                      {getFeatures().map((feat, idx) => (
                        <React.Fragment key={idx}>
                          {feat}{idx < getFeatures().length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </p>
                    <Link
                      to={productPath}
                      className="pricing-device-button" 
                    >
                      ADD TO CART
                    </Link>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* Included Section */}
      <section className="medical-alerts-included-section">
        <div className="medical-alerts-included-container">
          <div className="medical-alerts-included-content">
            <h2 className="medical-alerts-included-title">Included with All Alert1 Medical Alert Systems</h2>
            
            <div className="medical-alerts-included-list">
              <div className="medical-alerts-included-item">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="medical-alerts-included-text">
                  <strong>24/7/365</strong> Command Center Protection
                </p>
              </div>

              <div className="medical-alerts-included-item">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="medical-alerts-included-text">
                  <strong>Unlimited</strong> Button Pushes
                </p>
              </div>

              <div className="medical-alerts-included-item">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="medical-alerts-included-text">
                  <strong>Free</strong> Equipment Use
                </p>
              </div>

              <div className="medical-alerts-included-item">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="medical-alerts-included-text">
                  Works <strong>In & Around</strong> House
                </p>
              </div>

              <div className="medical-alerts-included-item">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="medical-alerts-included-text">
                  Coverage in <strong>All 50 States</strong>
                </p>
              </div>

              <div className="medical-alerts-included-item">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="medical-alerts-included-text">
                  Call <strong>911, Family</strong> and/or <strong>Friends</strong>
                </p>
              </div>
            </div>
          </div>

          <div className="medical-alerts-included-image">
            {includedImage ? (
              <img src={includedImage} alt="Woman with medical alert" className="medical-alerts-included-img" />
            ) : (
              <div className="medical-alerts-included-placeholder">
                <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                  <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                  <circle cx="200" cy="160" r="60" fill="#d0d0d0"/>
                  <ellipse cx="200" cy="300" rx="100" ry="120" fill="#d0d0d0"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* EZ Care In-Home Detail Section */}
      <section className="medical-alerts-detail-section">
        <div className="medical-alerts-detail-container">
          <div className="medical-alerts-detail-image-col">
            <Link
              to="/products/ez-care"
              className="medical-alerts-detail-image-link"
              aria-label="View EZ Care In-Home Medical Alert details"
            >
              {productImages['ez-care'] ? (
                <img src={productImages['ez-care']} alt="EZ Care In-Home Medical Alert" className="medical-alerts-detail-image" />
              ) : (
                <div className="medical-alerts-detail-placeholder">
                  <svg width="400" height="300" viewBox="0 0 400 300" fill="none">
                    <rect width="400" height="300" fill="#f0f0f0"/>
                  </svg>
                </div>
              )}
            </Link>
            <Link to="/products/ez-care" className="medical-alerts-detail-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>VIEW SYSTEM</Link>
          </div>
          
          <div className="medical-alerts-detail-content">
            <h2 className="medical-alerts-detail-title">
              <Link to="/products/ez-care" className="medical-alerts-detail-title-link">
                EZ Care In-Home Medical Alert
              </Link>
            </h2>
            <p className="medical-alerts-detail-text">
              Our in-home device allows you to safely age in place with peace of mind. The base unit is easy to install and consists of a small speaker box that EITHER plugs into a phone jack on your wall and an electrical outlet (if you have a landline) or simply into an electrical outlet (if you do not have a landline). Press your 100% showerproof button, and the medical alert system will connect to our Command Center.
            </p>
            
            <h3 className="medical-alerts-detail-subtitle">Includes:</h3>
            <div className="medical-alerts-detail-features">
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Fall detection (optional)</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span><strong>24/7</strong> US based emergency monitoring</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span><strong>Unlimited</strong> button pushes</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span><strong>100% showerproof</strong> help button</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Landline <strong>not</strong> required (wireless systems also available)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EZ Care Plus Detail Section */}
      <section className="medical-alerts-detail-section medical-alerts-detail-section-alt">
        <div className="medical-alerts-detail-container medical-alerts-detail-container-reverse">
          <div className="medical-alerts-detail-content">
            <h2 className="medical-alerts-detail-title">
              <Link to="/products/ez-care-plus" className="medical-alerts-detail-title-link">
                EZ Care Plus In-Home Medical Alert
              </Link>
            </h2>
            <p className="medical-alerts-detail-text">
              These medical alert devices include a medical alert pendant with built-in fall detection sensors. The pendant will place an automatic call for help if it senses a fall, even if you are unable to push the button. This technology does not detect 100% of falls, so we recommend that members always press their buttons if they are able.
            </p>
            
            <h3 className="medical-alerts-detail-subtitle">Includes:</h3>
            <div className="medical-alerts-detail-features">
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span><strong>Fall detection</strong> pendant</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span><strong>Two-way speaker</strong> and <strong>microphone</strong> in the <strong>base station</strong></span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span><strong>24/7</strong> US based emergency monitoring</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span><strong>100% showerproof</strong> button</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span><strong>Unlimited</strong> button pushes</span>
              </div>
            </div>
          </div>
          
          <div className="medical-alerts-detail-image-col">
            <Link
              to="/products/ez-care-plus"
              className="medical-alerts-detail-image-link"
              aria-label="View EZ Care Plus In-Home Medical Alert details"
            >
              {productImages['ez-care-plus'] ? (
                <img src={productImages['ez-care-plus']} alt="EZ Care Plus In-Home Medical Alert" className="medical-alerts-detail-image" />
              ) : (
                <div className="medical-alerts-detail-placeholder">
                  <svg width="400" height="300" viewBox="0 0 400 300" fill="none">
                    <rect width="400" height="300" fill="#f0f0f0"/>
                  </svg>
                </div>
              )}
            </Link>
            <Link to="/products/ez-care-plus" className="medical-alerts-detail-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>VIEW SYSTEM</Link>
          </div>
        </div>
      </section>

      {/* LifeBeacon Detail Section */}
      <section className="medical-alerts-detail-section">
        <div className="medical-alerts-detail-container">
          <div className="medical-alerts-detail-image-col">
            <Link
              to="/products/lifebeacon"
              className="medical-alerts-detail-image-link"
              aria-label="View LifeBeacon On-the-Go Pendant/Wristband Alert details"
            >
              {productImages['lifebeacon'] ? (
                <img src={productImages['lifebeacon']} alt="LifeBeacon On-the-Go" className="medical-alerts-detail-image" />
              ) : (
                <div className="medical-alerts-detail-placeholder">
                  <svg width="400" height="300" viewBox="0 0 400 300" fill="none">
                    <rect width="400" height="300" fill="#f0f0f0"/>
                  </svg>
                </div>
              )}
            </Link>
            <Link to="/products/lifebeacon" className="medical-alerts-detail-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>VIEW SYSTEM</Link>
          </div>
          
          <div className="medical-alerts-detail-content">
            <h2 className="medical-alerts-detail-title">
              <Link to="/products/lifebeacon" className="medical-alerts-detail-title-link">
                LifeBeacon On-the-Go Pendant/Wristband Alert
              </Link>
            </h2>
            <p className="medical-alerts-detail-text">
              Lightweight, stylish, and modern, our medical alert bracelet does not look like a traditional emergency alert device.
            </p>
            
            <h3 className="medical-alerts-detail-subtitle">Includes:</h3>
            <div className="medical-alerts-detail-features">
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Communicate directly through the device with 24/7 trained responders</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Wristband and lanyard wearable options</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Fashionable and comfortable unisex design</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Mobile alert with built-in location services</span>
              </div>
              <div className="medical-alerts-detail-feature">
                <svg className="medical-alerts-check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#28a745"/>
                  <path d="M7 12l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Optional Fall Detection feature</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="medical-alerts-cta-section">
        <div className="medical-alerts-cta-container">
          <p className="medical-alerts-cta-text">
            Call us today at <a href="tel:1-877-579-9862" className="medical-alerts-cta-phone">1-877-579-9862</a> and get peace of mind for you and your loved ones!
          </p>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MedicalAlertsPage;
